import { useState } from "react";
import { bool, func } from "prop-types";
import { observer } from "mobx-react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShareLinkModifiers } from "@utils";
import { saveShareSettingsQuery } from "@query";
import { useEditorWebsocket } from "@hooks";
import { useEstimateEditorStore, PresetPicker } from "@tools";
import { InfoLabel,  } from "@components";
import { Grid, Switch } from "@material-ui/core";

export const ShareLinkSettings = observer(({
  useVersionInUrl,
  handleVersionInLink,
}) => {
  
  const { t } = useTranslation();
  const { estimateUuid } = useParams();
  const editorStore = useEstimateEditorStore();
  const socket = useEditorWebsocket();
  
  const { noTurningOff, hideTurnedOffElements, hideBottomBar, hideExports } = editorStore;
  
  const [ isSendingSettings, setSendingSettings ] = useState(false);
  
  const handleAllowTurnOff = async () => {
    editorStore.setShareLinkSettings(
      ShareLinkModifiers.NO_TURN_OFF,
      !noTurningOff
    );
    await sendUpdatedShareLinkSettings();
  }
  
  const handleHideTurnedOffElements = async () => {
    editorStore.setShareLinkSettings(
      ShareLinkModifiers.HIDE_TURN_OFF,
      !hideTurnedOffElements
    );
    await sendUpdatedShareLinkSettings();
  }
  
  const handleHideExports = async () => {
    editorStore.setShareLinkSettings(
      ShareLinkModifiers.HIDE_EXPORTS,
      !hideExports
    );
    await sendUpdatedShareLinkSettings();
  }
  
  const handleHideBottomBar = async () => {
    editorStore.setShareLinkSettings(
      ShareLinkModifiers.HIDE_BOTTOM_BAR,
      !hideBottomBar
    );
    await sendUpdatedShareLinkSettings();
  }
  
  const sendUpdatedShareLinkSettings = async () => {
    setSendingSettings(true);
    socket?.requestShareSettingsUpdate(editorStore.shareLinkSettings);
    await saveShareSettingsQuery(estimateUuid, editorStore.shareLinkSettings);
    setSendingSettings(false);
  }
  
  return (
    <Grid
      item
      container
      direction="column"
      wrap="nowrap"
      alignItems="center"
    >
      <Grid item container justifyContent="space-between" className="mb-2">
        <InfoLabel label={t("views.editor.dialogs.share.settings.preset")} className="w-max m-0" />
        <PresetPicker
          commitPresetSelection
          noPresetCreate
        />
      </Grid>
      <Grid item container justifyContent="space-between" className="mb-2">
        <InfoLabel label={t("views.editor.dialogs.share.settings.include_version")} className="w-max m-0" />
        <Switch
          name="useVersionInUrl"
          color="primary"
          checked={useVersionInUrl}
          onChange={handleVersionInLink}
          disabled={isSendingSettings}
        />
      </Grid>
      <Grid item container justifyContent="space-between" className="mb-2">
        <InfoLabel label={t("views.editor.dialogs.share.settings.turn_off")} className="w-max m-0" />
        <Switch
          name="allowTurnOff"
          color="primary"
          checked={!noTurningOff}
          onChange={handleAllowTurnOff}
          disabled={isSendingSettings}
        />
      </Grid>
      <Grid item container justifyContent="space-between" className="mb-2">
        <InfoLabel label={t("views.editor.dialogs.share.settings.show_off")} className="w-max m-0" />
        <Switch
          name="hideTurnedOffElements"
          color="primary"
          checked={!hideTurnedOffElements}
          onChange={handleHideTurnedOffElements}
          disabled={isSendingSettings}
        />
      </Grid>
      <Grid item container justifyContent="space-between" className="mb-2">
        <InfoLabel label={t("views.editor.dialogs.share.settings.show_bottom_bar")} className="w-max m-0" />
        <Switch
          name="hideTurnedOffElements"
          color="primary"
          checked={!hideBottomBar}
          onChange={handleHideBottomBar}
          disabled={isSendingSettings}
        />
      </Grid>
      <Grid item container justifyContent="space-between" className="mb-2">
        <InfoLabel label={t("views.editor.dialogs.share.settings.show_exports")} className="w-max m-0" />
        <Switch
          name="hideExports"
          color="primary"
          checked={!hideExports}
          onChange={handleHideExports}
          disabled={isSendingSettings}
        />
      </Grid>
    </Grid>
  );
})

ShareLinkSettings.propTypes = {
  useVersionInUrl: bool.isRequired,
  handleVersionInLink: func.isRequired,
}